import { useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { AlertTriangle, CheckCircle, UserPlus, X } from 'lucide-react';
import { useAdminRegistrationStore } from '../../stores/adminRegistrationStore';
import { useWaitingListStore } from '../../stores/waitingListStore';
import { useDevis } from '../../lib/orders/useDevis';
import { produireEtEnvoyerFacture } from '../../lib/orders/gateway.supabase';
import { resultatInscriptionAdmin, type ResultatInscription } from '../../lib/orders/resultatInscriptionAdmin';
import { remainingPlaces } from '../../lib/places';
import type { SituationDeLaSemaine } from '../../lib/listeAttente/semaine';
import RecapitulatifInscription from './inscription/RecapitulatifInscription';
import Telephone from './Telephone';

export interface InscriptionEnAttente {
  /** La ligne de `waiting_list`. */
  attenteId: string;
  /** Où l'enfant est déjà inscrit cette semaine-là ; `null` tant que ce n'est pas lu. */
  cetteSemaine: SituationDeLaSemaine | null;
  parent: { id: string; email: string | null; prenom: string; nom: string; telephone: string | null };
  enfant: { id: string; prenom: string; nom: string };
  session: {
    id: string;
    stage: string;
    centre: string;
    /** Déjà mises en forme : « 19/10/2026 - 23/10/2026 ». */
    dates: string;
    capacite: number;
    inscrits: number;
  };
}

interface InscriptionDepuisAttenteModalProps {
  inscription: InscriptionEnAttente;
  onClose: () => void;
  /** Après une inscription : la liste et les places se relisent. */
  onInscrit: () => void;
}

/**
 * Inscrire un enfant de la liste d'attente, en une fenêtre.
 *
 * Le cas courant : l'équipe téléphone à la famille, la famille dit oui, et
 * l'administration valide l'inscription à sa place. Parent, enfant et
 * session sont déjà connus par la ligne d'attente ; il ne reste que le tarif
 * et les codes à vérifier.
 *
 * C'est le chemin de « Inscription admin », et lui seul : le même devis
 * (`quote_order`), la même commande (`create_order`, qui crée l'inscription
 * et la facture), le même envoi de la facture à la famille. La liste
 * d'attente ne crée ni prix ni facture à elle seule.
 */
const InscriptionDepuisAttenteModal = ({ inscription, onClose, onInscrit }: InscriptionDepuisAttenteModalProps) => {
  const { attenteId, cetteSemaine, parent, enfant, session } = inscription;
  const { createAdminRegistration, isSubmitting } = useAdminRegistrationStore();
  const { markEntryConverted } = useWaitingListStore();

  const [reduit, setReduit] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [resultat, setResultat] = useState<ResultatInscription | null>(null);

  const lignes = useMemo(() => [{ session_id: session.id, kid_id: enfant.id }], [session.id, enfant.id]);
  const { devis, chargement: devisEnCours, erreur: erreurDevis } = useDevis(parent.id, lignes, reduit, codes);

  const libres = remainingPlaces({ capacity: session.capacite, current_registrations: session.inscrits });

  const confirmer = async () => {
    setErreur(null);
    let inscrite: Awaited<ReturnType<typeof createAdminRegistration>>;
    try {
      inscrite = await createAdminRegistration(parent.id, [{
        id: `${session.id}-${enfant.id}`,
        activity_id: session.id,
        kid_id: enfant.id,
        kidName: `${enfant.prenom} ${enfant.nom}`,
        activityName: session.stage,
        activityCategory: session.centre,
        dateRange: session.dates,
      }], reduit, codes);
    } catch (err) {
      // Rien n'est écrit : la fenêtre reste ouverte, on peut corriger et recommencer.
      setErreur(err instanceof Error && err.message
        ? err.message
        : "Une erreur est survenue lors de la création de l'inscription.");
      return;
    }

    /*
      L'enfant est inscrit : il sort de la file AVANT l'envoi du courriel,
      pour qu'un envoi en échec ne le laisse pas « en attente » d'une place
      qu'il a déjà. Si ce marquage échoue, l'inscription tient quand même, et
      une seconde tentative serait refusée par `create_order` (« déjà
      inscrit ») : jamais de seconde facture. L'échec se dit.
    */
    let horsDeLaFile = true;
    try {
      await markEntryConverted(attenteId);
    } catch {
      horsDeLaFile = false;
    }

    const envoi = await produireEtEnvoyerFacture(inscrite.invoiceNumber, parent.email).catch((e: unknown) => ({
      pdfUrl: null,
      erreur: e instanceof Error ? e.message : "L'envoi de la facture a échoué.",
    }));

    const r = resultatInscriptionAdmin(inscrite.invoiceNumber, inscrite.commande, envoi, parent.email ?? '');
    setResultat(horsDeLaFile ? r : {
      type: 'error',
      message: `${r.message}\n\n⚠ La ligne de la liste d'attente n'a pas pu être marquée « convertie ». L'inscription est faite ; la ligne reste affichée.`,
    });
    onInscrit();
  };

  const fermer = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Dialog open onClose={fermer} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />

        <div className="relative w-full max-w-3xl rounded-lg bg-white p-4 shadow-xl sm:p-6">
          <div className="mb-4 flex items-start justify-between gap-4">
            <Dialog.Title className="flex items-center text-lg font-semibold">
              <UserPlus className="mr-2 h-5 w-5 text-teal-600" />
              Inscrire depuis la liste d'attente
            </Dialog.Title>
            <button onClick={fermer} className="text-gray-400 hover:text-gray-600" aria-label="Fermer">
              <X className="h-5 w-5" />
            </button>
          </div>

          <dl className="mb-6 grid gap-4 rounded-lg bg-gray-50 p-4 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs font-medium uppercase text-gray-500">Parent</dt>
              <dd className="mt-1 font-medium text-gray-900">{parent.prenom} {parent.nom}</dd>
              <dd className="break-all text-gray-600">{parent.email || 'Aucune adresse'}</dd>
              <dd className="text-gray-600"><Telephone numero={parent.telephone} /></dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase text-gray-500">Enfant</dt>
              <dd className="mt-1 font-medium text-gray-900">{enfant.prenom} {enfant.nom}</dd>
              {cetteSemaine?.cle === 'nulle-part' && (
                <dd className="mt-1 text-indigo-800">Inscrit nulle part cette semaine</dd>
              )}
              {cetteSemaine?.cle === 'deja-inscrit' && (
                <dd className="mt-1 text-gray-700">Déjà inscrit à cette session</dd>
              )}
              {cetteSemaine?.cle === 'ailleurs' && cetteSemaine.inscriptions.map((i) => (
                <dd key={i.id} className="mt-1 text-amber-900">
                  Inscrit cette semaine : {i.stage} · {i.centre}
                  {i.cancellation_status === 'requested' && ' (annulation demandée)'}
                </dd>
              ))}
            </div>
            <div>
              <dt className="text-xs font-medium uppercase text-gray-500">Session</dt>
              <dd className="mt-1 font-medium text-gray-900">{session.stage}</dd>
              <dd className="text-gray-600">{session.centre}</dd>
              <dd className="text-gray-600">{session.dates}</dd>
              <dd className="text-gray-600">
                {session.inscrits}/{session.capacite} inscrits
                {libres > 0 ? ` · ${libres} ${libres > 1 ? 'places libres' : 'place libre'}` : ' · complet'}
              </dd>
            </div>
          </dl>

          {resultat ? (
            <div className="flex flex-col items-center text-center">
              {resultat.type === 'success' ? (
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle className="h-7 w-7 text-green-600" />
                </div>
              ) : (
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
                  <AlertTriangle className="h-7 w-7 text-amber-600" />
                </div>
              )}
              <p className="mb-6 whitespace-pre-line text-sm text-gray-600">{resultat.message}</p>
              <button onClick={onClose} className="btn-primary px-6">Fermer</button>
            </div>
          ) : (
            <>
              {cetteSemaine?.cle === 'ailleurs' && (
                <div className="mb-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                  <p className="text-sm text-amber-800">
                    L'enfant a déjà une inscription cette semaine-là. Celle-ci s'y ajoute :
                    l'autre reste inscrite et facturée tant qu'elle n'est pas annulée.
                  </p>
                </div>
              )}

              {libres <= 0 && (
                <div className="mb-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                  <p className="text-sm text-amber-800">
                    La session est complète : l'inscription sera refusée tant qu'aucune place
                    n'est libre. Traitez d'abord l'annulation qui libère la place, ou augmentez
                    la capacité de la session dans la programmation.
                  </p>
                </div>
              )}

              {erreur && (
                <div className="mb-6 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
                  <p className="text-sm text-red-700">{erreur}</p>
                </div>
              )}

              <RecapitulatifInscription
                kidId={enfant.id}
                semaines={[{ sessionId: session.id, stage: session.stage, centre: session.centre, dates: session.dates }]}
                devis={devis}
                devisEnCours={devisEnCours}
                erreurDevis={erreurDevis}
                reduit={reduit}
                onBasculerReduit={() => setReduit(!reduit)}
                codes={codes}
                onCodes={setCodes}
                enCours={isSubmitting}
                onConfirmer={confirmer}
                onRetour={fermer}
                libelleRetour="Annuler"
              />
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default InscriptionDepuisAttenteModal;
